import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { getShouldValidatePersonalFinanceItem } from "@/hooks/useRequiredFields/utils/requiredFieldValues";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import regex from "../../utils/regex";
import { ERRORS_MESSAGE, frequencyForIncomeList, incomeTypesList, validFrequencyList } from "../../constants";
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import {
  saveValue,
  createIncome,
  deleteIncome,
  updateIncome,
  userDetailsSelector,
  setRequiredFieldsErrors,
} from "../../store/slices/applicationFormSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { getErrorToastPayload } from "../../utils/commonFunctions";

export default function Income({ income, customerId, applicationId, personalFinLoaders, isDisableEdit }) {
  const dispatch = useDispatch();
  const { isCustomerLogin } = useSelector(customerSelector);

  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      customer: { isIncomeShadingEnabled, incomeShading },
    },
  } = accessComponents;
  const { requireFieldErrors } = useSelector(userDetailsSelector);
  const [changeDetected, setChangeDetected] = useState(false);
  const [incomeList, setIncomeList] = useState(income || []);
  const isDisable = isCustomerLogin ? false : isDisableEdit;
  const isAddIncomeLoader = personalFinLoaders?.addIncomeLoader && personalFinLoaders?.id === customerId;

  useEffect(() => {
    setIncomeList(income || []);
  }, [income]);

  const validIncomeTypesList = incomeTypesList.map((item) => item.value);

  const validationSchema = Yup.object({
    incomeType: Yup.string()
      .oneOf(validIncomeTypesList, "Please select valid income type")
      .required("Income type is required"),
    amount: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .max(10, "Maximum of 7 digits"),
    frequency: Yup.string()
      .oneOf(validFrequencyList, "Please select valid frequency")
      .required("Frequency is required"),
    shading: Yup.number().optional(),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        const field = fieldName.split("_")[0];
        await validationSchema.validateAt(field, { [field]: value });
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: "",
          }),
        );
      } catch (error) {
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: error.message,
          }),
        );
      }
    }, 300),
    [],
  );

  const onAddNewIncome = () => {
    const data = {
      incomeType: "",
      frequency: "",
      amount: "",
      shading: 0,
      application: applicationId,
      customer: customerId,
    };
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: customerId,
        },
      }),
    );
    dispatch(createIncome({ data }));
  };

  const removeIncome = (Element) => {
    const data = {
      incomeId: Element._id,
      application: applicationId,
      customer: Element.customer,
    };
    delete data._id;
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: Element._id,
        },
      }),
    );
    dispatch(deleteIncome({ data })).then((res) => {
      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      }
    });
  };

  const handle = {
    onBlur: (name, value, newID) => {
      const temp = [...incomeList];

      temp.map((ele) => {
        if (ele._id === newID) {
          let updatedAssetData = {
            ...ele,
            [name]: value,
            incomeId: newID,
            application: applicationId,
            customer: customerId,
          };
          delete updatedAssetData._id;
          if (changeDetected) {
            dispatch(updateIncome({ data: updatedAssetData }));
            setChangeDetected(false);
          }
          return updatedAssetData;
        } else {
          return ele;
        }
      });
    },
    onChange: async (field, value, newID, index) => {
      const name = field.split("_")[0];
      debouncedValidation(field, value);
      const temp = [...incomeList];

      let isChangeDetected = false;

      const updateIncome = temp.map((ele) => {
        if (!ele._id) {
          const dataWithOutId = {
            ...ele,
            [name]: value,
            application: applicationId,
            customer: customerId,
          };
          isChangeDetected = true;
          dispatch(createIncome({ data: dataWithOutId }));
        }
        if (ele._id === newID) {
          const updatedIncomeData = {
            ...ele,
            [name]: value,
          };
          if (ele[name] !== value) {
            isChangeDetected = true;
          }
          return updatedIncomeData;
        } else {
          return ele;
        }
      });
      setChangeDetected(isChangeDetected);

      if (isChangeDetected) {
        setIncomeList(updateIncome);
      }
    },
  };

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({ fieldError, localState: {}, fieldName, localFieldName, doNotShowError });
  // Lender errors only show on validated rows: the first row and any row holding data.
  const validatedRows = incomeList.map((item, index) =>
    getShouldValidatePersonalFinanceItem({ listName: "income", item, index }),
  );

  return (
    <>
      <Grid container alignItems={"center"} justifyContent="start" mt={2} mb={1}>
        <Grid item>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1d4164" }}>
            Income (net)
          </Typography>
        </Grid>
      </Grid>
      {incomeList?.map((ele, index) => {
        const isInventmentProperty = ele?.incomeType === "Investment property";
        return (
          <Grid container spacing={1} mb={"20px"} key={ele?._id}>
            <Grid container item xs={12} spacing={1}>
              <Grid item xs={12} sm={3.5}>
                <FormControl fullWidth variant="filled">
                  <InputLabel
                    id={`income-type-label-${ele?._id}`}
                    error={getLocalAndLenderError({
                      fieldName: `income.[0].incomeType_${customerId}_${index}`,
                      doNotShowError: !!ele?.incomeType || !validatedRows[index],
                    })}
                  >
                    Income type
                  </InputLabel>
                  <Select
                    size="small"
                    labelId={`income-type-label-${ele?._id}`}
                    id={`income_type_${ele?._id}`}
                    disabled={isDisable}
                    value={ele?.incomeType}
                    label="Income type"
                    name={`incomeType_${customerId}_${index}`}
                    onChange={(event) => {
                      handle.onChange(`incomeType_${customerId}_${index}`, event.target.value, ele?._id, index);
                    }}
                    onBlur={() => handle.onBlur("incomeType", ele?.incomeType, ele?._id)}
                    error={getLocalAndLenderError({
                      fieldName: `income.[0].incomeType_${customerId}_${index}`,
                      doNotShowError: !!ele?.incomeType || !validatedRows[index],
                    })}
                  >
                    {incomeTypesList.map((i) => (
                      <MenuItem value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({
                    fieldName: `income.[0].incomeType_${customerId}_${index}`,
                    doNotShowError: !!ele?.incomeType || !validatedRows[index],
                  }) && (
                    <FormHelperText sx={{ color: "#d32f2f" }}>
                      {getLocalAndLenderError({
                        fieldName: `income.[0].incomeType_${customerId}_${index}`,
                        doNotShowError: !!ele?.incomeType || !validatedRows[index],
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`income-amount-${ele?._id}`}
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Income amount"
                  variant="filled"
                  style={{ width: "100%" }}
                  value={ele?.amount}
                  fullWidth
                  disabled={isDisable}
                  name={`amount_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `income.[0].amount_${customerId}_${index}`,
                    doNotShowError: !!ele?.amount || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `income.[0].amount_${customerId}_${index}`,
                    doNotShowError: !!ele?.amount || !validatedRows[index],
                  })}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) =>
                    handle.onChange(`amount_${customerId}_${index}`, event.target.value, ele?._id, index)
                  }
                  onBlur={() => handle.onBlur("amount", currencyMaskedValue(ele?.amount), ele?._id)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled">
                  <InputLabel
                    id={`income-freq-label-${ele?._id}`}
                    error={getLocalAndLenderError({
                      fieldName: `income.[0].frequency_${customerId}_${index}`,
                      doNotShowError: !!ele?.frequency || !validatedRows[index],
                    })}
                  >
                    Income frequency
                  </InputLabel>
                  <Select
                    size="small"
                    labelId={`income-freq-label-${ele?._id}`}
                    id={`income_freq_${ele?._id}`}
                    value={ele?.frequency}
                    label="Income frequency"
                    disabled={isDisable}
                    name={`frequency_${customerId}_${index}`}
                    onChange={(event) => {
                      handle.onChange(`frequency_${customerId}_${index}`, event.target.value, ele?._id, index);
                    }}
                    onBlur={() => handle.onBlur("frequency", ele?.frequency, ele?._id)}
                    error={getLocalAndLenderError({
                      fieldName: `income.[0].frequency_${customerId}_${index}`,
                      doNotShowError: !!ele?.frequency || !validatedRows[index],
                    })}
                  >
                    {frequencyForIncomeList.map((i) => (
                      <MenuItem value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({
                    fieldName: `income.[0].frequency_${customerId}_${index}`,
                    doNotShowError: !!ele?.frequency || !validatedRows[index],
                  }) && (
                    <FormHelperText sx={{ color: "#d32f2f" }}>
                      {getLocalAndLenderError({
                        fieldName: `income.[0].frequency_${customerId}_${index}`,
                        doNotShowError: !!ele?.frequency || !validatedRows[index],
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {isIncomeShadingEnabled && isInventmentProperty ? (
                <Grid item xs={12} sm={2} sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={typeof ele?.shading === "number" ? Number(ele.shading) > 0 : Boolean(ele?.shading)}
                        onChange={(event) => {
                          const value = event.target.checked ? incomeShading : 0;
                          handle.onChange(`shading_${customerId}_${index}`, value, ele?._id, index);
                        }}
                        onBlur={() => handle.onBlur("shading", Number(ele?.shading) || 0, ele?._id)}
                        disabled={isDisable}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Shading (80%)"
                    labelPlacement="right"
                    sx={{ ml: 0, "& .MuiFormControlLabel-label": { fontSize: "0.82rem" } }}
                  />
                </Grid>
              ) : (
                <></>
              )}

              <Grid item xs={12} sm={0.5}>
                <IconButton
                  size="small"
                  aria-label={`Remove income ${index + 1}`}
                  title={
                    personalFinLoaders?.removeIncomeLoader && personalFinLoaders?.id === ele?._id
                      ? "Removing..."
                      : "Remove income"
                  }
                  sx={{ marginTop: "10px", color: "error.main" }}
                  onClick={() => removeIncome(ele)}
                  disabled={isDisable || personalFinLoaders?.removeIncomeLoader}
                >
                  <DeleteOutlineIcon fontSize="medium" />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <div>
          <Button
            onClick={() => onAddNewIncome()}
            variant="outlined"
            size="small"
            disabled={isAddIncomeLoader || isDisable}
          >
            {isAddIncomeLoader ? "Adding..." : "Add income"}
          </Button>
        </div>
      </Grid>
    </>
  );
}
